import React from 'react';
import AiChart from '../components/AiChart';
import MessageBubble from '../components/MessageBubble';

const API_URL = 'https://heybiseo.ai.kr';

const SUGGESTIONS = [
  { icon: '💰', text: '오늘 매출이 어떻게 됐어?' },
  { icon: '👥', text: '이번 달 신규 회원이 몇 명이야?' },
  { icon: '📊', text: '이번 달 매출 차트 보여줘' },
  { icon: '⚠️', text: '미납 회원 현황 알려줘' },
  { icon: '📈', text: '회원 등록 추이 분석해줘' },
  { icon: '💡', text: '매출 올릴 방법 추천해줘' },
];

function AiReportPage({ token }) {
  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [gymData, setGymData] = React.useState(null);
  const [dataStatus, setDataStatus] = React.useState('loading');
  const [usageInfo, setUsageInfo] = React.useState({ used: 0, limit: 10, remaining: 10 });
  const chatEndRef = React.useRef(null);
  const textareaRef = React.useRef(null);

  // Chart.js 로드 + 데이터 fetch
  React.useEffect(() => {
    if (!window.Chart) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js';
      document.head.appendChild(script);
    }
    loadGymData();
  }, []);

  React.useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  async function loadGymData() {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const [dashRes, membersRes, paymentsRes, usageRes] = await Promise.allSettled([
        fetch(`${API_URL}/dashboard/summary?tenant_id=1`, { headers }),
        fetch(`${API_URL}/members/?limit=200&tenant_id=1`, { headers }),
fetch(`${API_URL}/payment/list?tenant_id=1`, { headers }),
        fetch(`${API_URL}/ai-report/usage?tenant_id=1`, { headers }),
      ]);

      const data = {};
      if (dashRes.status === 'fulfilled' && dashRes.value.ok)
        data.dashboard = await dashRes.value.json();
      if (membersRes.status === 'fulfilled' && membersRes.value.ok)
        data.members = await membersRes.value.json();
      if (paymentsRes.status === 'fulfilled' && paymentsRes.value.ok)
        data.payments = await paymentsRes.value.json();
      if (usageRes.status === 'fulfilled' && usageRes.value.ok)
        setUsageInfo(await usageRes.value.json());

      setGymData(data);
      setDataStatus(Object.keys(data).length > 0 ? 'ok' : 'error');
    } catch {
      setGymData({});
      setDataStatus('error');
    }
  }

  function buildGymContext() {
    if (!gymData || Object.keys(gymData).length === 0) return '현재 연결된 데이터 없음';
    const now = new Date();
    let ctx = '';

    if (gymData.dashboard)
      ctx += '[ 대시보드 ]\n' + JSON.stringify(gymData.dashboard, null, 2) + '\n\n';

    if (gymData.members) {
      const list = Array.isArray(gymData.members)
        ? gymData.members
        : (gymData.members.members || gymData.members.data || []);
      const active = list.filter(x => x.status === 'active' || x.membership_status === 'active');
      const recent = list.filter(x =>
        x.created_at && (now - new Date(x.created_at)) < 30 * 24 * 3600 * 1000
      );
      ctx += `[ 회원 현황 ]\n총 ${list.length}명 / 활성 ${active.length}명 / 최근30일 신규 ${recent.length}명\n`;
      ctx += '회원 목록(최근10명): ' + JSON.stringify(
        list.slice(0, 10).map(x => ({
          name: x.name, status: x.status || x.membership_status,
          phone: x.phone, created_at: x.created_at,
        }))
      ) + '\n\n';
    }

    if (gymData.payments) {
      const list = Array.isArray(gymData.payments)
        ? gymData.payments
        : (gymData.payments.payments || gymData.payments.data || []);
      const todayStr = now.toISOString().slice(0, 10);
      const monthStr = now.toISOString().slice(0, 7);
      const today = list.filter(x => x.payment_date?.startsWith(todayStr));
      const month = list.filter(x => x.payment_date?.startsWith(monthStr));
      ctx += `[ 결제 현황 ]\n오늘: ${today.length}건 / ${today.reduce((s, x) => s + (x.amount || 0), 0).toLocaleString()}원\n`;
      ctx += `이번달: ${month.length}건 / ${month.reduce((s, x) => s + (x.amount || 0), 0).toLocaleString()}원\n`;
      ctx += '최근결제(5건): ' + JSON.stringify(
        list.slice(0, 5).map(x => ({
          member: x.member_name, amount: x.amount,
          date: x.payment_date, product: x.product_name,
        }))
      ) + '\n';
    }
    return ctx;
  }

  async function sendMessage(overrideText) {
    const userText = overrideText || input.trim();
    if (!userText || isLoading) return;
    if (usageInfo.remaining <= 0) {
      alert('오늘 AI 대화 횟수를 모두 사용하셨습니다. 내일 다시 이용해 주십시오.');
      return;
    }

    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    const newHistory = [...messages, { role: 'user', content: userText }];
    setMessages(newHistory);
    setIsLoading(true);

    try {
      const res = await fetch(`${API_URL}/ai-report/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ messages: newHistory, gym_context: buildGymContext(), tenant_id: 1 }),
      });

      if (res.status === 429) {
        const err = await res.json();
        setMessages([...newHistory, { role: 'assistant', content: err.detail }]);
        setIsLoading(false);
        return;
      }
      if (!res.ok) throw new Error('서버 오류');

      const data = await res.json();
      setMessages([...newHistory, { role: 'assistant', content: data.reply, chart_data: data.chart_data }]);
      setUsageInfo({ used: data.used, limit: 10, remaining: data.remaining });
    } catch (e) {
      setMessages([...newHistory, { role: 'assistant', content: '오류: ' + e.message }]);
    }
    setIsLoading(false);
  }

  const statusColor = { loading: '#F59E0B', ok: '#16A34A', error: '#EF4444' };
  const statusText  = { loading: '데이터 로딩 중...', ok: '실시간 연동됨', error: '연결 실패' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fff' }}>

      {/* 헤더 */}
      <div style={{ padding: '14px 20px 12px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <span style={{ fontSize: 20, fontWeight: 700, color: '#2563EB' }}>Hey</span>
        <span style={{ fontSize: 16, fontWeight: 500, color: '#1E293B' }}>비서 리포트</span>
        <span style={{ fontSize: 11, padding: '2px 8px', background: '#EFF6FF', color: '#1D4ED8', borderRadius: 99, border: '1px solid #BFDBFE' }}>AI</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#94A3B8' }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor[dataStatus], display: 'inline-block' }} />
          {statusText[dataStatus]}
        </div>
      </div>

      {/* 채팅 영역 */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 8px', display: 'flex', flexDirection: 'column', gap: 14 }}>

        {messages.length === 0 && (
          <>
            <div style={{ textAlign: 'center', padding: '20px 0 8px' }}>
              <div style={{ fontSize: 17, fontWeight: 500, color: '#1E293B', marginBottom: 6 }}>
                대표님, 안녕하십니까!
              </div>
              <div style={{ fontSize: 13, color: '#94A3B8', lineHeight: 1.6 }}>
                30년 경험을 바탕으로 헬스장 운영을 도와드리겠습니다.<br />
                아래 질문을 선택하시거나 직접 입력해 주십시오.
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {SUGGESTIONS.map((s, i) => (
                <button key={i} onClick={() => sendMessage(s.text)}
                  style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 10, padding: '10px 12px', textAlign: 'left', cursor: 'pointer', fontSize: 12, color: '#475569', lineHeight: 1.5 }}>
                  <span style={{ fontSize: 14, display: 'block', marginBottom: 3 }}>{s.icon}</span>
                  {s.text}
                </button>
              ))}
            </div>
          </>
        )}

        {messages.map((m, i) => <MessageBubble key={i} msg={m} />)}

        {isLoading && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>AI</div>
            <div style={{ background: '#F8FAFC', borderRadius: '4px 12px 12px 12px', padding: '14px 16px', display: 'flex', gap: 5 }}>
              {[0, 0.15, 0.3].map((d, i) => (
                <span key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: '#93C5FD', display: 'inline-block', animation: `aiDot 1.2s ${d}s infinite` }} />
              ))}
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* 입력 영역 */}
      <div style={{ padding: '10px 16px 16px', borderTop: '1px solid #F1F5F9', flexShrink: 0 }}>
        <div style={{ fontSize: 11, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4, color: usageInfo.remaining <= 3 ? '#EF4444' : '#94A3B8' }}>
          오늘 남은 대화
          <span style={{ fontWeight: 600, color: usageInfo.remaining <= 3 ? '#EF4444' : '#2563EB' }}>
            {usageInfo.remaining}회
          </span>
          / {usageInfo.limit}회
          {usageInfo.remaining <= 3 && usageInfo.remaining > 0 && <span style={{ color: '#EF4444' }}>⚠ 거의 소진</span>}
          {usageInfo.remaining === 0 && <span style={{ color: '#EF4444' }}>✕ 오늘 마감</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={e => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
            }}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder={usageInfo.remaining === 0 ? '오늘 대화 횟수를 모두 사용하셨습니다.' : '질문해 주십시오... (Shift+Enter: 줄바꿈)'}
            disabled={usageInfo.remaining === 0}
            rows={1}
            style={{ flex: 1, resize: 'none', border: '1px solid #E2E8F0', borderRadius: 8, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', lineHeight: 1.5, minHeight: 38, maxHeight: 100 }}
          />
          <button onClick={() => sendMessage()} disabled={isLoading || !input.trim() || usageInfo.remaining === 0}
            style={{ width: 36, height: 36, flexShrink: 0, border: 'none', borderRadius: 8, cursor: 'pointer', background: (isLoading || !input.trim() || usageInfo.remaining === 0) ? '#F1F5F9' : '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill={(isLoading || !input.trim() || usageInfo.remaining === 0) ? '#CBD5E1' : 'white'}>
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
        </div>
      </div>

      <style>{`@keyframes aiDot { 0%,60%,100%{transform:translateY(0);opacity:.4} 30%{transform:translateY(-5px);opacity:1} }`}</style>
    </div>
  );
}

export default AiReportPage;